/* Bespoke Hero (Sakaya) - interactive hotspots.
   When idle, tooltips auto-reveal one at a time in sequence (~2.6s each).
   Hovering a pin pauses the cycle and shows that one (CSS :hover); leaving
   resumes. Click/tap toggles a pin (works on touch) and pauses briefly.
   Outside tap / Escape closes. No dependencies; scoped per section. */
(() => {
  'use strict';

  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const CYCLE_MS = 2600; // time each tooltip stays open during the idle cycle

  const initHero = (root) => {
    if (root.dataset.bheroInit === 'true') return;
    root.dataset.bheroInit = 'true';

    const hots = Array.from(root.querySelectorAll('.bhero__hot'));
    const closeAll = () => hots.forEach((h) => h.classList.remove('is-open'));

    // ---- Idle auto-cycle: reveal one tooltip at a time ----
    let idx = 0;
    let paused = false;
    let timer = null;
    let resumeTimer = null;

    const showOnly = (i) => {
      closeAll();
      if (hots[i]) hots[i].classList.add('is-open');
    };
    const tick = () => {
      if (paused || !hots.length) return;
      showOnly(idx);
      idx = (idx + 1) % hots.length;
    };
    const stopCycle = () => {
      if (timer) {
        clearInterval(timer);
        timer = null;
      }
    };
    const startCycle = () => {
      if (reduceMotion || hots.length < 2) return;
      stopCycle();
      tick(); // reveal the first one immediately
      timer = window.setInterval(tick, CYCLE_MS);
    };
    const pause = () => {
      paused = true;
      closeAll();
    };
    const resume = () => {
      paused = false;
    };

    hots.forEach((hot) => {
      // Hovering / focusing a pin pauses the cycle; CSS :hover shows that one.
      hot.addEventListener('mouseenter', pause);
      hot.addEventListener('mouseleave', resume);
      hot.addEventListener('focusin', pause);
      hot.addEventListener('focusout', resume);

      const pin = hot.querySelector('.bhero__pin');
      if (!pin) return;
      pin.addEventListener('click', (e) => {
        e.stopPropagation();
        paused = true; // user took control
        const isOpen = hot.classList.contains('is-open');
        closeAll();
        if (!isOpen) hot.classList.add('is-open');
        // Resume idle cycling after a beat (covers touch: no mouseleave fires).
        if (resumeTimer) clearTimeout(resumeTimer);
        resumeTimer = window.setTimeout(resume, 6000);
      });
    });

    // Outside tap / Escape closes any open popover.
    document.addEventListener('click', closeAll);
    root.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') closeAll();
    });

    // Theme editor: open + hold the hotspot block being edited.
    document.addEventListener('shopify:block:select', (e) => {
      if (!root.contains(e.target) || !e.target.classList.contains('bhero__hot')) return;
      paused = true;
      closeAll();
      e.target.classList.add('is-open');
    });
    document.addEventListener('shopify:block:deselect', (e) => {
      if (!root.contains(e.target)) return;
      e.target.classList.remove('is-open');
      resume();
    });

    // Pause the cycle while the tab is hidden (saves work, avoids jumpy resume).
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) stopCycle();
      else startCycle();
    });

    startCycle();

    // Scroll cue without an explicit anchor: glide to the next section.
    const cue = root.querySelector('[data-bhero-next]');
    if (cue) {
      cue.addEventListener('click', () => {
        const section = root.closest('.shopify-section') || root;
        const next = section.nextElementSibling;
        if (next) next.scrollIntoView({ behavior: 'smooth', block: 'start' });
      });
    }
  };

  const boot = () => document.querySelectorAll('[data-bhero]').forEach(initHero);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // Re-init when a merchant adds/edits the section in the theme editor.
  document.addEventListener('shopify:section:load', (e) => {
    e.target.querySelectorAll('[data-bhero]').forEach(initHero);
  });
})();
